import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const now = new Date();

export default function Bills() {
  const [bills, setBills] = useState([]);
  const [filterMonth, setFilterMonth] = useState(now.getMonth() + 1);
  const [filterYear, setFilterYear] = useState(now.getFullYear());
  const [filterType, setFilterType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ type: 'Electricity', month: now.getMonth() + 1, year: now.getFullYear(), amount: '', status: 'Unpaid', notes: '' });
  const [msg, setMsg] = useState('');

  const load = () =>
    api.get('/bills', { params: { month: filterMonth, year: filterYear, type: filterType || undefined } })
      .then(r => setBills(r.data));

  useEffect(() => { load(); }, [filterMonth, filterYear, filterType]);

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await api.post('/bills', form);
      setShowModal(false);
      setMsg('Bill added!');
      load();
    } catch (err) { setMsg(err.response?.data?.message || 'Error'); }
  };

  const togglePaid = async (bill) => {
    const newStatus = bill.status === 'Paid' ? 'Unpaid' : 'Paid';
    await api.put(`/bills/${bill._id}`, { status: newStatus, paidDate: newStatus === 'Paid' ? new Date() : null });
    load();
  };

  const deleteBill = async (id) => {
    if (!window.confirm('Delete this bill?')) return;
    await api.delete(`/bills/${id}`);
    load();
  };

  const totalBills = bills.reduce((s, b) => s + b.amount, 0);
  const paidBills = bills.filter(b => b.status === 'Paid').reduce((s, b) => s + b.amount, 0);

  return (
    <Layout title="Utility Bills">
      {msg && <div className="alert alert-info">{msg} <button style={{ float:'right', background:'none', border:'none', cursor:'pointer' }} onClick={() => setMsg('')}>✕</button></div>}

      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3,1fr)', maxWidth: 500 }}>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#d69e2e', color: '#fff' }}>⚡</div>
          <div className="stat-info"><h3 style={{ fontSize: 16 }}>PKR {totalBills.toLocaleString()}</h3><p>Total Bills</p></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#38a169', color: '#fff' }}>✅</div>
          <div className="stat-info"><h3 style={{ fontSize: 16 }}>PKR {paidBills.toLocaleString()}</h3><p>Paid</p></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#e53e3e', color: '#fff' }}>⚠️</div>
          <div className="stat-info"><h3 style={{ fontSize: 16 }}>PKR {(totalBills - paidBills).toLocaleString()}</h3><p>Unpaid</p></div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="card-title">⚡ Bills — {MONTHS[filterMonth - 1]} {filterYear}</h3>
          <div className="flex gap-10">
            <select value={filterMonth} onChange={e => setFilterMonth(e.target.value)}>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
            <select value={filterYear} onChange={e => setFilterYear(e.target.value)}>
              {[2024, 2025, 2026, 2027].map(y => <option key={y}>{y}</option>)}
            </select>
            <select value={filterType} onChange={e => setFilterType(e.target.value)}>
              <option value="">All Types</option>
              <option>Electricity</option>
              <option>Gas</option>
            </select>
            <button className="btn btn-primary btn-sm" onClick={() => setShowModal(true)}>➕ Add Bill</button>
          </div>
        </div>

        <div className="table-wrapper">
          <table>
            <thead>
              <tr><th>Type</th><th>Month</th><th>Amount</th><th>Status</th><th>Notes</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {bills.length === 0 ? (
                <tr><td colSpan={6} className="text-center" style={{ padding: 30, color: '#718096' }}>No bills for this period</td></tr>
              ) : bills.map(b => (
                <tr key={b._id}>
                  <td>
                    <span className={`badge ${b.type === 'Electricity' ? 'badge-warning' : 'badge-info'}`}>
                      {b.type === 'Electricity' ? '⚡' : '🔥'} {b.type}
                    </span>
                  </td>
                  <td>{MONTHS[b.month - 1]} {b.year}</td>
                  <td><strong>PKR {b.amount.toLocaleString()}</strong></td>
                  <td><span className={`badge ${b.status === 'Paid' ? 'badge-success' : 'badge-danger'}`}>{b.status}</span></td>
                  <td style={{ fontSize: 12 }}>{b.notes || '-'}</td>
                  <td>
                    <div className="flex gap-10">
                      <button className={`btn btn-sm ${b.status === 'Paid' ? 'btn-warning' : 'btn-success'}`} onClick={() => togglePaid(b)}>
                        {b.status === 'Paid' ? '↩ Unpaid' : '✅ Mark Paid'}
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => deleteBill(b._id)}>🗑</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Bill</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleAdd}>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Bill Type *</label>
                    <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}>
                      <option>Electricity</option>
                      <option>Gas</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Month</label>
                    <select value={form.month} onChange={e => setForm(f => ({ ...f, month: e.target.value }))}>
                      {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Year</label>
                    <select value={form.year} onChange={e => setForm(f => ({ ...f, year: e.target.value }))}>
                      {[2024, 2025, 2026, 2027].map(y => <option key={y}>{y}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Amount (PKR) *</label>
                    <input type="number" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} required min="0" />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                      <option>Unpaid</option>
                      <option>Paid</option>
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn: '1/-1' }}>
                    <label>Notes</label>
                    <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
                  </div>
                </div>
                <div className="form-actions">
                  <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="btn btn-primary">💾 Save</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
